import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { EntityOptions } from '@redux/modules/types'

import useTaskListSection, { useTaskListSectionForm } from '@hooks/useTaskListSection'

import { priorityKeys, prioritySettings } from '@functions/status'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import EntitySelector from '@components/EntitySelector'
import OrganizationSelector from '@components/OrganizationSelector'

const MODAL_KEY = 'CreateOrEditTaskListSectionModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTaskListSection: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  requestOptions?: EntityOptions,
  showModal: boolean,
}

const CreateOrEditTaskListSectionModal = (props: ModalProps) => {
  const { callbacks, requestOptions, showModal } = props
  const { closeModal, createTaskListSection } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const taskListSection = digObject(modalPayload, 'taskListSection', {})

  const newRecord = !taskListSection.id

  const {
    callbacks: {
      createTaskListSection: createFn,
      updateTaskListSection: updateFn,
    },
    creating,
    updating,
  } = useTaskListSection(taskListSection)

  const {
    entityState,
    entityState: {
      checklist_template_id,
      debtor_id,
      debtor_type,
      due_date,
      priority,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useTaskListSectionForm(taskListSection, { validateOn: 'change' })

  // To be used with handleCallbackAction
  const createTaskListSectionPayload: HandleCallbackActionParams = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions,
    toastText: `Task List Section ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Update'}
        titleSecondLine="Task List Section"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <EntitySelector
            callbacks={{
              selectItem: taskListTemplate => setEntityState({
                checklist_template_id: taskListTemplate?.id,
              }),
            }}
            label="* Checklist Template"
            entityKey="taskListTemplates"
            marginTop="large"
            searchRequestOptions={{
              include_global_task_list_templates: true,
              template_checklist: true,
            }}
            selectedItemId={checklist_template_id}
          />

          <SidebarNoticeBox boxProps={{ marginBottom: 0, marginTop: 'large' }}>
            <SidebarNoticeBox.Title>
              Bulk Update Items
            </SidebarNoticeBox.Title>

            <SidebarNoticeBox.Paragraph>
              By changing the fields below it will update all items in the group.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>

          <Form.Field label="Priority" errorMessage={errors.priority} marginTop="large">
            <select name="priority" value={priority} {...handlers}>
              <option value="">Please Select...</option>
              {priorityKeys.map((priorityKey) => {
                const { label } = prioritySettings[priorityKey]

                return (
                  <option key={priorityKey} value={priorityKey}>
                    {label}
                  </option>
                )
              })}
            </select>
          </Form.Field>

          <Form.Field label="* Debtor Type" marginTop="large">
            <select
              name="debtor_type"
              value={debtor_type}
              onChange={e => setEntityState({ debtor_id: undefined, debtor_type: e.target.value })}
            >
              <option value="">Please Select...</option>
              <option value="Agency">Agency</option>
              <option value="Brand">Brand</option>
              <option value="Provider">Provider</option>
            </select>
          </Form.Field>

          {!!debtor_type && (
            <OrganizationSelector
              callbacks={{
                selectItem: ({ entity }) => setEntityState({ debtor_id: entity?.id }),
              }}
              entityType={debtor_type}
              label="* Debtor"
              marginTop="large"
              selectedItemId={debtor_id}
            />
          )}

          <Form.Field label="Due Date" marginTop="large">
            <input name="due_date" type="date" value={due_date} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={() => createTaskListSection(createTaskListSectionPayload)}
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Section` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditTaskListSectionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
